import {
  PageProps,
  getLinks,
  useStoryblokState,
} from '@cinch-nx/data-storyblok'
import {
  PolicyPageFeature,
  PolicyPageStoryContent,
  getPolicyPageContentData,
  policiesStaticPaths,
} from '@cinch-nx/landing-feature-policy-page'
import Head from '@cinch-nx/landing-ui-shared/lib/head/head'
import { GetStaticPaths, GetStaticProps } from 'next'

export function PolicyPage({
  story,
  preview,
  generatedAt,
}: PageProps<PolicyPageStoryContent>) {
  const { content } = useStoryblokState({ story, enableBridge: preview })

  const SEO = content?.SEO?.[0]

  return (
    <>
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle={`${content.title} - cinch`}
        fallbackDescription="We're the faff-free car service that puts you in the driving seat. We help you find and buy used cars – quickly and easily. Buy online with free home delivery or from an approved dealer."
      />
      {story.content && <PolicyPageFeature content={content} />}
    </>
  )
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  try {
    const slug = params?.slug as string
    const story = await getPolicyPageContentData(slug, preview)

    if (!story) {
      return { notFound: true } as const
    }

    return {
      props: {
        story,
        preview,
        layoutProps: {
          showBorderRadius: true,
        },
      },
      revalidate: 60, // In seconds
    }
  } catch (error) {
    console.log(error)
    return { notFound: true } as const
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Get the paths we want to pre-render based on data
  try {
    const links = await getLinks('policies')

    const filteredLinks = Object.fromEntries(
      Object.entries(links).filter(
        ([, link]) => !link.slug.includes('policies/modern-slavery-statement'),
      ),
    )
    const paths = policiesStaticPaths({ links: filteredLinks })
    return { paths, fallback: 'blocking' }
  } catch {
    return { paths: [], fallback: 'blocking' }
  }
}

export default PolicyPage
